import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const fieldControlClass =
  'oche-interactive rounded-md border border-[var(--color-line)] bg-[var(--color-surface)] focus:border-[var(--color-oche)]/50';

const inputClass = cn(fieldControlClass, 'w-full px-3 py-2');

/** Info control - hover or focus on ? reveals the tooltip bubble. */
export function FieldTooltipIcon({
  tipId,
  text,
  placement = 'bottom',
  align = 'center',
}: {
  tipId: string;
  text: string;
  /** Tooltip side relative to the ? icon. */
  placement?: 'top' | 'bottom';
  /** Horizontal alignment - use `end` for controls near the right edge of the header. */
  align?: 'center' | 'end';
}) {
  return (
    <span className="field-tooltip group/icon relative z-50 inline-flex overflow-visible">
      <button
        type="button"
        className="oche-press flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[var(--color-line)] bg-[var(--color-canvas)] text-[10px] font-bold text-[var(--color-muted)] hover:border-[var(--color-oche)]/50 hover:text-[var(--color-chalk)]"
        aria-label={`Help: ${text}`}
        aria-describedby={tipId}
      >
        ?
      </button>
      <span
        role="tooltip"
        id={tipId}
        className={cn(
          'pointer-events-none absolute z-[100] w-56 rounded-md border border-[var(--color-line)] bg-[var(--color-surface)] px-3 py-2 text-xs leading-snug text-[var(--color-chalk)] opacity-0 shadow-lg transition-opacity duration-[var(--duration-fast)] group-hover/icon:opacity-100 group-focus-within/icon:opacity-100',
          placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2',
          align === 'end' ? 'right-0' : 'left-1/2 -translate-x-1/2',
        )}
      >
        {text}
      </span>
    </span>
  );
}

type LabelRowProps = {
  label: string;
  tooltip: string;
  htmlFor?: string;
  tipId: string;
};

function FieldLabelRow({ label, tooltip, htmlFor, tipId }: LabelRowProps) {
  return (
    <span className="flex items-center gap-1.5">
      <label htmlFor={htmlFor} className="font-medium">
        {label}
      </label>
      <FieldTooltipIcon tipId={tipId} text={tooltip} />
    </span>
  );
}

export type TextFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'title'> & {
  label: string;
  /** Required helper text - shown as tooltip and aria-describedby. */
  tooltip: string;
};

export function TextField({ label, tooltip, id, className, ...props }: TextFieldProps) {
  const uid = useId();
  const inputId = id ?? uid;
  const tipId = `${inputId}-tip`;

  return (
    <div className="field-root grid gap-1.5 text-sm">
      <FieldLabelRow label={label} tooltip={tooltip} htmlFor={inputId} tipId={tipId} />
      <input
        id={inputId}
        title={tooltip}
        aria-describedby={tipId}
        className={cn(inputClass, className)}
        {...props}
      />
    </div>
  );
}

export type SelectFieldProps = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'title'> & {
  label: string;
  tooltip: string;
  children: React.ReactNode;
  /** Hide visible label (keep tooltip + aria). */
  hideLabel?: boolean;
};

export function SelectField({
  label,
  tooltip,
  id,
  className,
  children,
  hideLabel,
  ...props
}: SelectFieldProps) {
  const uid = useId();
  const selectId = id ?? uid;
  const tipId = `${selectId}-tip`;

  return (
    <div className="field-root grid gap-1.5 text-sm">
      {hideLabel ? (
        <span className="sr-only">
          <FieldLabelRow label={label} tooltip={tooltip} htmlFor={selectId} tipId={tipId} />
        </span>
      ) : (
        <FieldLabelRow label={label} tooltip={tooltip} htmlFor={selectId} tipId={tipId} />
      )}
      <select
        id={selectId}
        title={tooltip}
        aria-label={hideLabel ? label : undefined}
        aria-describedby={tipId}
        className={cn(inputClass, className)}
        {...props}
      >
        {children}
      </select>
    </div>
  );
}

export type FileFieldProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'title' | 'type'> & {
  label: string;
  tooltip: string;
};

export function FileField({
  label,
  tooltip,
  id,
  className,
  onChange,
  disabled,
  accept,
  ...props
}: FileFieldProps) {
  const uid = useId();
  const inputId = id ?? uid;
  const tipId = `${inputId}-tip`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    setFileName(file?.name ?? null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      if (file?.type.startsWith('image/')) return URL.createObjectURL(file);
      return null;
    });
    onChange?.(e);
  }

  return (
    <div className={cn('field-root grid gap-1.5 text-sm', className)}>
      <FieldLabelRow label={label} tooltip={tooltip} htmlFor={inputId} tipId={tipId} />
      <div className="flex flex-wrap items-center gap-3">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            width={56}
            height={56}
            className="h-14 w-14 shrink-0 rounded-full border border-[var(--color-line)] bg-[var(--color-line)] object-cover oche-enter-fade"
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="oche-press shrink-0"
            title={tooltip}
            aria-describedby={tipId}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {fileName ? 'Change file' : 'Choose file'}
          </Button>
          <span
            className={cn(
              'min-w-0 truncate text-xs',
              fileName ? 'text-[var(--color-chalk)]' : 'text-[var(--color-muted)]',
            )}
            title={fileName ?? undefined}
          >
            {fileName ?? 'No file chosen'}
          </span>
        </div>
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        title={tooltip}
        aria-describedby={tipId}
        aria-label={label}
        disabled={disabled}
        className="sr-only"
        onChange={handleChange}
        {...props}
      />
    </div>
  );
}
